import type { PagedSourceConfig, ReportColumn } from '../types';
import { selectFilter } from '../utils';

/**
 * Columns that only mean something after the missing-data run (Source 2 / Source 3).
 * Ticking any of these (or their B twins, which follow automatically) makes the page ask the user to
 * press Start before Export is enabled.
 *
 * ⚠️ firstName / surname are here on purpose: on the list they are the REQUESTER's name, and the
 * person needing counselling can be someone else.
 */
export const REQUEST_ENRICHED_KEYS = new Set([
  'firstName', 'surname', 'email', 'phone', 'gender', 'dateOfBirth', 'missingFields', 'filledFrom',
]);

/** Person B duplicates. Same order as the Person A columns; no checkbox of their own. */
const twinColumns: ReportColumn[] = [
  { key: 'firstNameB', label: 'First Name (B)', category: 'Person' },
  { key: 'surnameB', label: 'Surname (B)', category: 'Person' },
  { key: 'emailB', label: 'Email (B)', category: 'Person' },
  { key: 'phoneB', label: 'Phone (B)', category: 'Person' },
  { key: 'genderB', label: 'Gender (B)', category: 'Person' },
  { key: 'dateOfBirthB', label: 'Date of Birth (B)', category: 'Person', type: 'date' },
];

export const request: PagedSourceConfig = {
  mode: 'paged',
  label: 'Request',
  plural: 'Requests',
  columns: [
    { key: 'reference', label: 'Reference', category: 'Request', defaultVisible: true },
    { key: 'requestID', label: 'ID', category: 'Request' },
    // ⚠️ From the list this is the requester's name, not necessarily the person needing care.
    { key: 'fullName', label: 'Requester Full Name', category: 'Person', defaultVisible: true },
    { key: 'firstName', label: 'First Name', category: 'Person', twinKey: 'firstNameB' },
    { key: 'surname', label: 'Surname', category: 'Person', twinKey: 'surnameB' },
    { key: 'email', label: 'Email', category: 'Person', twinKey: 'emailB' },
    { key: 'phone', label: 'Phone', category: 'Person', twinKey: 'phoneB' },
    { key: 'gender', label: 'Gender', category: 'Person', twinKey: 'genderB' },
    { key: 'dateOfBirth', label: 'Date of Birth', category: 'Person', type: 'date', twinKey: 'dateOfBirthB' },
    { key: 'needName', label: 'Counselling Need', category: 'Request', defaultVisible: true },
    { key: 'locationName', label: 'Location', category: 'Church', defaultVisible: true },
    { key: 'statusName', label: 'Status', category: 'Request', defaultVisible: true },
    { key: 'submittedDateTime', label: 'Submitted', category: 'Request', type: 'datetime', defaultVisible: true },
    { key: 'ageDisplay', label: 'Age', category: 'Request' },
    { key: 'isExistingMember', label: 'Existing Member', category: 'Person' },
    { key: 'missingFields', label: 'Missing fields', category: 'Data quality', trailing: true },
    { key: 'filledFrom', label: 'Filled from', category: 'Data quality', trailing: true },
  ],
  twinColumns,
  filters: [
    selectFilter('status', 'Status', 'All statuses'),
    selectFilter('need', 'Counselling Need', 'All needs', undefined, 'needID'),
    selectFilter('location', 'Location', 'All locations', undefined, 'locationID'),
    { key: 'submittedFrom', field: 'submittedDateTime', label: 'Submitted from', type: 'date', op: 'gte', placeholder: 'Any date' },
    { key: 'submittedTo', field: 'submittedDateTime', label: 'Submitted to', type: 'date', op: 'lte', placeholder: 'Any date' },
  ],
};
